import { ApiClient } from '@driverforce/api-client';
import type {
  ApiResponse,
  ChatAttachment,
  ChatConversation,
  ChatConversationListItem,
  ChatMessage,
  HealthCheckResponse,
  TelegramRecipient,
  TelegramRecipientInput,
} from '@driverforce/shared-types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class AdminApiClient extends ApiClient {
  healthCheck(): Promise<ApiResponse<HealthCheckResponse>> {
    return this.get<HealthCheckResponse>('/health');
  }

  // ── Chat (admin) ────────────────────────────────────────────────────────
  listChatConversations(params: {
    search?: string;
    filter?: 'all' | 'unread';
    page?: number;
    pageSize?: number;
  } = {}): Promise<
    ApiResponse<{
      items: ChatConversationListItem[];
      total: number;
      page: number;
      pageSize: number;
    }>
  > {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.filter) q.set('filter', params.filter);
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    const qs = q.toString();
    return this.get(`/chat/conversations${qs ? `?${qs}` : ''}`);
  }

  listChatConversationMessages(
    conversationId: string,
    params: { before?: string; limit?: number } = {},
  ): Promise<ApiResponse<{ messages: ChatMessage[]; hasMore: boolean }>> {
    const q = new URLSearchParams();
    if (params.before) q.set('before', params.before);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return this.get(
      `/chat/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`,
    );
  }

  postChatConversationMessage(
    conversationId: string,
    payload: { body?: string; clientNonce: string; attachmentIds?: string[] },
  ): Promise<ApiResponse<{ message: ChatMessage }>> {
    return this.post(`/chat/conversations/${conversationId}/messages`, payload);
  }

  markChatConversationRead(
    conversationId: string,
    upToMessageId: string,
  ): Promise<ApiResponse<{ ok: true }>> {
    return this.post(`/chat/conversations/${conversationId}/read`, {
      upToMessageId,
    });
  }

  openChatConversation(
    userId: string,
  ): Promise<ApiResponse<{ conversation: ChatConversation }>> {
    return this.post(`/chat/conversations`, { userId });
  }

  uploadChatAttachment(
    file: File,
  ): Promise<ApiResponse<{ attachment: ChatAttachment }>> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.postFormData('/chat/attachments', fd);
  }

  getChatAttachmentDownload(
    id: string,
  ): Promise<ApiResponse<{ url: string; expiresIn: number }>> {
    return this.get(`/chat/attachments/${id}/download`);
  }

  // ── Telegram notification recipients (admin) ─────────────────────────────
  getTelegramRecipients(): Promise<ApiResponse<{ recipients: TelegramRecipient[] }>> {
    return this.get('/admin/telegram/recipients');
  }

  addTelegramRecipient(
    payload: TelegramRecipientInput,
  ): Promise<ApiResponse<{ recipient: TelegramRecipient }>> {
    return this.post('/admin/telegram/recipients', payload);
  }

  deleteTelegramRecipient(chatId: string): Promise<ApiResponse<{ removed: boolean }>> {
    return this.delete(`/admin/telegram/recipients/${encodeURIComponent(chatId)}`);
  }
}

export const api = new AdminApiClient(API_BASE_URL);
